// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {RoomOutfitSlot} from '@fluxer/constants/src/RoomOutfit';
import type {
	CosmeticCatalogResponse,
	CosmeticItemListResponse,
	EquippedCosmeticsResponse,
	RoomCharacterEquipResponse,
	RoomOutfitResponse,
	UserCosmeticsResponse,
} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';

// The settings tab shows every type at once, so one untyped request replaces three typed ones.
export async function fetchCatalog(): Promise<CosmeticCatalogResponse> {
	const response = await http.get<CosmeticCatalogResponse>(Endpoints.COSMETICS_CATALOG);
	return response.body;
}

// Point reads by ID, items taken off sale included; the server takes at most 50 IDs.
export async function fetchItems(ids: ReadonlyArray<string>): Promise<CosmeticItemListResponse> {
	const response = await http.get<CosmeticItemListResponse>(Endpoints.COSMETICS_ITEMS, {query: {ids: ids.join(',')}});
	return response.body;
}

export async function fetchUserCosmetics(): Promise<UserCosmeticsResponse> {
	const response = await http.get<UserCosmeticsResponse>(Endpoints.USER_COSMETICS);
	return response.body;
}

// Snowflakes go out as strings; the shared request schema types them as parsed (bigint).
export type EquipCosmeticsBody = Partial<
	Record<'avatar_decoration_id' | 'avatar_ultra_id' | 'profile_effect_id' | 'nameplate_id', string | null>
>;

// The worn avatar decoration, ultra decoration and nameplate come back through USER_UPDATE, like every other user field.
export async function equip(changes: EquipCosmeticsBody): Promise<EquippedCosmeticsResponse> {
	const response = await http.put<EquippedCosmeticsResponse>(Endpoints.USER_COSMETICS_EQUIPPED, {body: changes});
	return response.body;
}

// The room character has its own endpoint and rate limit; null wears the default character, shirt null derives its
// colour from the user ID.
export async function equipRoomCharacter(
	itemId: string | null,
	shirt: number | null,
): Promise<RoomCharacterEquipResponse> {
	const response = await http.put<RoomCharacterEquipResponse>(Endpoints.USER_COSMETICS_ROOM_CHARACTER, {
		body: {item_id: itemId, shirt},
	});
	return response.body;
}

// Snowflakes as strings; absent slots stay as worn, null empties one. shirt null derives the colour from the user ID.
export type RoomOutfitBody = Partial<Record<`${RoomOutfitSlot}_id`, string | null>> & {shirt?: number | null};

// Every slot goes in one request: the server writes the outfit at once and takes the worn whole character off.
export async function equipRoomOutfit(body: RoomOutfitBody): Promise<RoomOutfitResponse> {
	const response = await http.put<RoomOutfitResponse>(Endpoints.USER_COSMETICS_ROOM_OUTFIT, {body});
	return response.body;
}
